/**
 * TemplateContext — scope chain for variable resolution during template rendering.
 *
 * Each block iteration (BEGIN/END) creates a child context whose parent is the
 * enclosing scope.  Variable look-ups walk up the chain automatically so inner
 * templates can reference outer data without explicit plumbing.
 */
export class TemplateContext {
    /**
     * Creates a new template context scope.
     *
     * @param {Object} data   - The data object for this scope level.
     * @param {TemplateContext|null} parent - Parent context (null for root).
     */
    constructor(data, parent = null) {
        this.data = data
        this.parent = parent
    }

    /**
     * Resolves a dot-separated key path against this scope, bubbling up to
     * ancestors if the key is not found locally.
     *
     * @param {string[]} path - Key segments, e.g. ["user", "name"].
     * @returns {*} The resolved value, or undefined.
     */
    resolve(path) {
        if (path.length === 0) return undefined

        // _parent.x.y — explicit parent reference
        if (path[0] === "_parent") {
            return this.parent ? this.parent.resolve(path.slice(1)) : undefined
        }

        let value = this.data
        for (const key of path) {
            if (value == null) {
                // Not found here — try parent scope
                return this.parent ? this.parent.resolve(path) : undefined
            }
            value = value[key]
        }

        // If the final value is undefined, bubble up to parent
        if (value === undefined && this.parent) {
            return this.parent.resolve(path)
        }

        return value
    }

    /**
     * Evaluates whether a path resolves to a truthy value.
     * Empty arrays are considered falsy.
     *
     * @param {string[]} path - Key segments to resolve.
     * @returns {boolean} True if the resolved value is truthy.
     */
    isTruthy(path) {
        const value = this.resolve(path)
        if (Array.isArray(value)) return value.length > 0
        return !!value
    }

    /**
     * Creates a child context for a loop iteration or partial rendering.
     *
     * @param {Object} data   - Item data for this iteration.
     * @param {Object} extras - Auto-injected helpers (_index, _count, etc.).
     * @returns {TemplateContext} A new child context.
     */
    createChild(data, extras = {}) {
        return new TemplateContext({ ...data, ...extras }, this)
    }
}
